import Link from 'next/link';

import { Icon, IconButton, Menu } from '@zillow/constellation-10';
import { IconMoreVerticalFilled } from '@zillow/constellation-icons';

import { toAppRoute } from '../../../constants/urls';

import type { Route } from 'nextjs-routes';
import type {
  AccountViewListingOptionType,
  AccountViewPropertyOptionDto,
  AccountViewPropertyUnitOptionDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type OverflowMenuItem = {
  label: string;
  href?: Route;
  onSelect?: () => void;
};

type ApiOverflowOption = AccountViewPropertyOptionDto | AccountViewPropertyUnitOptionDto;

/**
 * Builds overflow menu items from API row options. Uses a `Link` href when `ctaUrl`
 * is a non-empty app path (does not contain `api/web`); otherwise uses `actionOverrides`.
 */
export const buildOverflowMenuItems = (
  options: Array<ApiOverflowOption>,
  {
    actionOverrides = {},
  }: {
    actionOverrides?: Partial<Record<AccountViewListingOptionType, () => void>>;
  } = {},
): Array<OverflowMenuItem> =>
  options.map(({ type, ctaText, ctaUrl }) => {
    const override = actionOverrides[type];
    // TODO: BE will remove the api/web api paths
    const shouldNavigate = Boolean(ctaUrl) && !ctaUrl.includes('api/web');

    return {
      label: ctaText,
      ...(shouldNavigate ? { href: toAppRoute(ctaUrl) } : { onSelect: override }),
    };
  });

type Props = {
  items: Array<OverflowMenuItem>;
  triggerLabel: string;
  disabled?: boolean;
};

const OverflowMenu = ({ items, triggerLabel, disabled = false }: Props) => {
  const stopRowNavigation = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Menu.Root>
      <Menu.Trigger>
        <IconButton
          aria-label={triggerLabel}
          tone="brand"
          emphasis="bare"
          title={triggerLabel}
          size="md"
          disabled={disabled}
          onClick={stopRowNavigation}
          data-testid="OverflowMenu-Trigger"
        >
          <Icon render={<IconMoreVerticalFilled />} />
        </IconButton>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content>
          {items.map((item) =>
            item.href ? (
              <Menu.Item key={item.label} asChild onClick={(event) => event.stopPropagation()}>
                <Link href={item.href}>{item.label}</Link>
              </Menu.Item>
            ) : (
              <Menu.Item
                key={item.label}
                onClick={(event) => {
                  stopRowNavigation(event);
                  item.onSelect?.();
                }}
              >
                <Menu.ItemLabel>{item.label}</Menu.ItemLabel>
              </Menu.Item>
            ),
          )}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
};

export { OverflowMenu };
